import { DEFAULT_USER_SETTINGS, isSameDate } from "@taskfortress/core"
import React, { useCallback, useMemo } from "react"
import { useData } from "../../providers/DataProvider"
import { Box } from "../Box"
import { Drawer } from "../Drawer"
import { Text } from "../Text"
import { Pressable } from "../gluestack/pressable"
import { DatePickerCore } from "./DatePickerCore"
import { type QuickDateOption, getQuickDateOptions } from "./quickDateOptions"

interface DatePickerMobileProps {
  isVisible: boolean
  onClose: () => void
  selectedDate: string
  onDateSelect: (date: string) => void
  explicitSelectRequired?: boolean
  minDate?: string
  maxDate?: string
  disabledDates?: string[]
  showTaskIndicators?: boolean
}

export const DatePickerMobile: React.FC<DatePickerMobileProps> = ({
  isVisible,
  onClose,
  selectedDate,
  onDateSelect,
  explicitSelectRequired = false,
  minDate,
  maxDate,
  disabledDates,
  showTaskIndicators = false,
}) => {
  const { userSettings, tasks } = useData()
  const quickDateOptions = useMemo(() => getQuickDateOptions(), [])

  const handleDateSelect = useCallback(
    (date: string) => {
      onDateSelect(date)
      if (!explicitSelectRequired) {
        onClose()
      }
    },
    [onDateSelect, onClose, explicitSelectRequired],
  )

  const handleQuickDateSelect = useCallback(
    (option: QuickDateOption) => {
      handleDateSelect(option.date)
    },
    [handleDateSelect],
  )

  return (
    <Drawer
      isVisible={isVisible}
      onClose={onClose}
      title="Select Date"
      size="lg"
      anchor="bottom"
      contentClassName="z-50"
      closeOnBackdropPress={false}
    >
      <Box className="space-y-4">
        {/* Quick Date Options - Vertical Layout */}
        <Box className="space-y-2">
          {quickDateOptions.map((option) => {
            const isSelected = isSameDate(selectedDate, option.date)
            return (
              <Pressable
                key={option.label}
                onPress={() => handleQuickDateSelect(option)}
                className="flex-row items-center justify-between py-3 px-4 rounded-lg bg-background-50 border border-outline-200"
              >
                <Text
                  className={`text-sm font-medium ${
                    isSelected ? "text-primary-500" : "text-typography-900"
                  }`}
                >
                  {option.label}
                </Text>
                <Text
                  className={`text-sm ${
                    isSelected ? "text-primary-400" : "text-typography-600"
                  }`}
                >
                  {option.dayOfWeek}
                </Text>
              </Pressable>
            )
          })}
        </Box>

        {/* Calendar */}
        <DatePickerCore
          selectedDate={selectedDate}
          onDateSelect={handleDateSelect}
          minDate={minDate}
          maxDate={maxDate}
          disabledDates={disabledDates}
          showTaskIndicators={showTaskIndicators}
          tasks={tasks}
          firstDayOfWeek={
            userSettings.firstDayOfWeek || DEFAULT_USER_SETTINGS.firstDayOfWeek!
          }
        />
      </Box>
    </Drawer>
  )
}
